"use client";

import { useEffect } from "react";
import { useCurrentLocation } from "@/hooks/use-current-location";
import {
  consumePendingRouteScroll,
  prepareRouteScrollRestore,
} from "@/lib/navigation-scroll";

/**
 * How long a restore waits on a page that has stopped getting taller before it
 * accepts this is all the height there is. Comfortably longer than a streamed
 * section or one infinite page arriving, short enough that a genuinely shorter
 * page stops fighting quickly.
 */
const GROWTH_PATIENCE_MS = 1_200;

/**
 * The ceiling, for a page that grows forever (an infinite list whose sentinel
 * we are parked on and whose end we will never reach). Long enough for the ten
 * or so round trips it takes to rebuild a deeply scrolled list from a cold
 * cache; any user input cuts it short regardless.
 */
const RESTORE_HARD_CAP_MS = 10_000;

/** Any of these means the reader took over; the restore steps aside. */
const ABORT_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

export function RouteScrollRestorer() {
  const { pathname, search } = useCurrentLocation();

  // A back gesture — the phone's edge swipe, the OS button, the browser's own
  // arrow — never touches <BackButton />, so nothing stages a restore for it.
  //
  // The browser does restore window scroll on popstate, but only against the
  // page as it exists at that moment. That is enough for a route Next kept
  // mounted, and useless for one it rebuilt: /discover's Advanced tab comes
  // back holding whatever pages its query cache still has, so the document is
  // a fraction of its old height, the browser clamps to the top, and the
  // reader lands at the first card again. Staging the same record the Back
  // button stages makes the gesture a backstop rather than a second class of
  // navigation — and when the browser *did* get it right, the restore reads
  // the position as already reached and stops on its first frame.
  useEffect(() => {
    const stageRestore = () => {
      prepareRouteScrollRestore(
        `${window.location.pathname}${window.location.search}`,
      );
    };

    window.addEventListener("popstate", stageRestore);
    return () => window.removeEventListener("popstate", stageRestore);
  }, []);

  useEffect(() => {
    const pending = consumePendingRouteScroll(`${pathname}${search}`);
    if (!pending) return;

    let frame = 0;
    let lastHeight = document.documentElement.scrollHeight;
    let lastGrowthAt = performance.now();
    const hardDeadline = lastGrowthAt + RESTORE_HARD_CAP_MS;

    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      for (const event of ABORT_EVENTS) {
        window.removeEventListener(event, stop);
      }
    };

    // The page a back press returns to is rarely its final height on the first
    // frame. Next's own scroll handler runs, RSC content streams in, media
    // resolves — and an infinite list comes back holding only the pages its
    // cache still has, so the document can be a fraction of the height the
    // reader left it at. A fixed pair of frames restored against whatever
    // existed then and gave up; this keeps asking until the document can
    // actually hold the position.
    //
    // Re-asking is also what walks an infinite list back down: each clamped
    // attempt parks the scroll at the bottom, where the list's own load-more
    // sentinel is, so the next page arrives, the document grows, and the next
    // attempt gets further. Ten pages of people is ten server round trips, so
    // the budget is "still growing" rather than a fixed window — a page that
    // has stopped growing has given everything it is going to give, and a page
    // that is still loading has not. It stops the instant the position is
    // reached, and the instant the reader touches the page, because a restore
    // that fights a live scroll is worse than one that gives up.
    const attempt = () => {
      window.scrollTo(pending.x, pending.y);
      if (Math.abs(window.scrollY - pending.y) <= 1) {
        stop();
        return;
      }

      const now = performance.now();
      const height = document.documentElement.scrollHeight;
      if (height > lastHeight) {
        lastHeight = height;
        lastGrowthAt = now;
      }

      if (now >= hardDeadline || now - lastGrowthAt >= GROWTH_PATIENCE_MS) {
        stop();
        return;
      }
      frame = requestAnimationFrame(attempt);
    };

    for (const event of ABORT_EVENTS) {
      window.addEventListener(event, stop, { passive: true });
    }
    frame = requestAnimationFrame(attempt);

    return stop;
  }, [pathname, search]);

  return null;
}
